import * as d3 from 'd3';
import { ScaleLinear } from 'd3';
import React, { useMemo, useRef } from 'react';

import { DataPoint } from '../../PriceChangesChart.types';

export interface PriceChangesChartLinePlotProps {
  data: DataPoint[];
  xScale: ScaleLinear<number, number>;
  yScale: ScaleLinear<number, number>;
  color: string;
  dashed?: boolean;
  tooltipDate?: Date;
  dateToIndexMap?: Map<number, number>;
}

const linePath =
  (
    xScale: ScaleLinear<number, number>,
    yScale: ScaleLinear<number, number>,
    dateToIndexMap?: Map<number, number>
  ) =>
  (ts: DataPoint[]) =>
    d3
      .line<DataPoint>()
      .defined(d => d.value !== undefined)
      .x(d => {
        if (!dateToIndexMap) {
          return xScale(d.date.getTime());
        }

        const index = dateToIndexMap.get(d.date.getTime());
        return index !== undefined ? xScale(index) : 0;
      })
      .y(d => (d.value !== undefined ? yScale(d.value) : 0))
      .curve(d3.curveLinear)(ts);

export const PriceChangesChartLinePlot = ({
  data,
  xScale,
  yScale,
  color,
  dashed,
  tooltipDate,
  dateToIndexMap,
}: PriceChangesChartLinePlotProps) => {
  const pathRef = useRef<SVGPathElement>(null);

  const path = useMemo(
    () => linePath(xScale, yScale, dateToIndexMap)(data),
    [data, xScale, yScale, dateToIndexMap]
  );

  const selectedData = data.find(
    d => d.date.valueOf() === tooltipDate?.valueOf()
  );

  const optionalDashProp = dashed ? { strokeDasharray: '2' } : {};

  return (
    <g>
      <path
        ref={pathRef}
        data-testid="line-plot"
        vectorEffect="non-scaling-stroke"
        d={path ?? ''}
        stroke={color}
        fill="none"
        {...optionalDashProp}
      />
      {selectedData?.value !== undefined && (
        <circle
          cx={(() => {
            if (!dateToIndexMap) {
              return xScale(selectedData.date.getTime());
            }

            const index = dateToIndexMap.get(selectedData.date.getTime());
            return index !== undefined ? xScale(index) : 0;
          })()}
          cy={yScale(selectedData.value)}
          r="4"
          fill={color}
          stroke="white"
          data-testid="selected-point"
        />
      )}
    </g>
  );
};
